<template>
    <div>
        <ul v-if="!useChart">
            <li v-for="month in durationTotalsPerMonth" :key="month.name">
                <template v-if="month.data.length > 0">
                    {{ getDateFormat(month) }}
                    <ul>
                        <li v-for="item in month.data" :key="item.name">
                            {{ item.name }}: {{ item.accumulator }}
                        </li>
                    </ul>
                </template>
                <template v-else
                    >{{ getDateFormat(month) }}: geen registraties</template
                >
            </li>
        </ul>
        <usage-chart
            v-if="useChart && chartConfig && displayRows"
            :rows="displayRows"
            :chartType="chartType"
            :columnSettings="displayColumnSettings"
            :chartOptions="chartOptions"
        />
    </div>
</template>
<script>
    import { mapActions } from 'vuex';
    import UsageChart from './UsageChart.vue';
    export default {
        components: { 'usage-chart': UsageChart },
        props: {
            organizationId: { type: Number, required: true },
            chartConfig: { type: Object, required: true },
            themeProfileId: { type: Number, required: false },
            useChart: { type: Boolean, default: true },
            start: { type: Date, required: false },
            end: { type: Date, required: false }
        },

        data: function () {
            return { chartData: null };
        },

        mounted: function () {
            this.init();
        },

        watch: {
            async start() {
                this.chartData = null;
                this.chartData = await this.getChartData();
            },
            async end() {
                this.chartData = null;
                this.chartData = await this.getChartData();
            }
        },

        computed: {
            chartType() {
                return this.chartConfig.chartType;
            },

            chartSettings() {
                return this.chartConfig.settings;
            },

            chartOptions() {
                return this.chartSettings.options;
            },

            resultsTable() {
                if (!this.chartData) {
                    return null;
                }
                return this.chartData?.tables?.[0];
            },

            resultsColumns() {
                if (!this.resultsTable) {
                    return null;
                }
                return this.resultsTable.columns;
            },

            resultsRows() {
                if (!this.resultsColumns) {
                    return null;
                }

                const resultsTable = this.resultsTable;
                if (this.themeProfileId) {
                    const customDataColumnIndex =
                        this.findColumnIndex('customDimensions');
                    return resultsTable.rows.filter((row) => {
                        const profiles = row[customDataColumnIndex]?.profiles;
                        return profiles?.includes(this.themeProfileId);
                    });
                }
                return resultsTable.rows;
            },

            durationTotalsPerMonth() {
                if (!this.resultsRows) {
                    return null;
                }
                const {
                    accumulator,
                    name: accumulatorName,
                    formatter
                } = this.chartSettings.data;

                const accumulatorArr = accumulator.split('.');
                const accumulatorNameArr = accumulatorName.split('.');

                const simplifyDateToSimpleMonthString = (datetime) =>
                    `${datetime.getFullYear()}${datetime.getMonth()}`;
                const makeDurationObjectFromRows = (rows) => {
                    return rows
                        .reduce((acc, item) => {
                            const itemNameBase =
                                item[this.findColumnIndex(accumulatorNameArr[0])];
                            const itemName =
                                accumulatorNameArr.length === 2
                                    ? itemNameBase[accumulatorNameArr[1]]
                                    : itemNameBase;

                            const itemaccumulatorBase =
                                item[this.findColumnIndex(accumulatorArr[0])];
                            const itemaccumulator =
                                accumulatorArr.length === 2
                                    ? itemaccumulatorBase[accumulatorArr[1]]
                                    : itemaccumulatorBase;

                            const pageItem = acc.find(
                                ({ name }) => name === itemName
                            );
                            if (pageItem) {
                                pageItem.accumulator =
                                    pageItem.accumulator + itemaccumulator;
                            } else {
                                acc.push({
                                    name: itemName,
                                    accumulator: itemaccumulator
                                });
                            }
                            return acc;
                        }, [])
                        .map((pageEntry) => ({
                            ...pageEntry,
                            accumulator: formatter
                                ? formatter(pageEntry.accumulator)
                                : pageEntry.accumulator
                        }));
                };
                const monthsToShow = this.calculateMonthsToShow;
                let currentDate = this.end ? new Date() : this.end;
                const pastSixMonthsArray = [...new Array(monthsToShow)]
                    .map((__, index) => {
                        const md = new Date();
                        md.setMonth(currentDate.getMonth() - index, 1);
                        return md;
                    })
                    .map(simplifyDateToSimpleMonthString);
                const monthsBucket = pastSixMonthsArray.reduce((acc, item) => {
                    acc[item] = [];
                    return acc;
                }, {});

                this.resultsRows.forEach((resultItem) => {
                    const timestamp = resultItem[this.findColumnIndex('timestamp')];
                    const bucketName = simplifyDateToSimpleMonthString(timestamp);
                    const bucketToUse = monthsBucket[bucketName];
                    bucketToUse?.push(resultItem);
                });

                return pastSixMonthsArray
                    .map((bucketName) => {
                        const year = bucketName.slice(0, 4);
                        const month = bucketName.slice(4);
                        const monthDate = new Date(year, month);
                        return {
                            name: bucketName,
                            year,
                            month,
                            monthDate,
                            displayDate: monthDate.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'long'
                            }),
                            data: makeDurationObjectFromRows(
                                monthsBucket[bucketName]
                            )
                        };
                    })
                    .sort((a, b) => {
                        return b.monthDate - a.monthDate;
                    });
            },

            displaySections() {
                if (!this.durationTotalsPerMonth) {
                    return null;
                }
                return [
                    ...this.durationTotalsPerMonth.reduce((acc, item) => {
                        item.data?.forEach(({ name }) => acc.add(name));
                        return acc;
                    }, new Set())
                ];
            },

            displayRows() {
                if (!this.resultsRows) {
                    return null;
                }
                const rows = [];
                this.durationTotalsPerMonth.forEach((monthData) => {
                    const pageDurationThisMonth = this.displaySections.map(
                        (pageName) =>
                            monthData.data.find(({ name }) => name === pageName)
                                ?.accumulator || 0
                    );
                    const date = this.getDateFormat(monthData);
                    rows.push([date, ...pageDurationThisMonth]);
                });
                return rows.reverse();
            },

            displayColumnSettings() {
                if (!this.displaySections) {
                    return null;
                }

                return [
                    { name: 'Maand', type: 'string' },
                    ...this.displaySections.map((rowSetting) => ({
                        name: rowSetting,
                        type: 'number'
                    }))
                ];
            },
            calculateMonthsToShow() {
                let monthsToUse = 6;
                if (this.start && this.end) {
                    // calculate number of months difference
                    // Number of years multiplied by 12 helps to correct if start month in previous year before end month
                    // select dec 2022 and jan 2023:
                    // 2023 - 2022 = 12 month
                    // jan - dec   = -11 months
                    //             = 1 months (and add always 1)
                    let yearsDiff =
                        this.end.getFullYear() - this.start.getFullYear();
                    monthsToUse =
                        this.end.getMonth() -
                        this.start.getMonth() +
                        1 +
                        yearsDiff * 12;
                }
                return monthsToUse;
            }
        },

        methods: {
            ...mapActions(['getUsageChartData']),
            async init() {
                this.chartData = await this.getChartData();
            },

            getDateFormat(monthData) {
                let month = this.$t(
                    `dashboard.statistieken.months.${monthData.month}`
                );
                let year = monthData.year;
                // more then 18 months: show mrt '23, apr '23, enz.
                const monthsToShow = this.calculateMonthsToShow;
                if (monthsToShow > 18) {
                    month = month.substring(0, 3);
                    if (month == 'maa') {
                        month = 'mrt';
                    }
                }
                // more then 12 months: show maart '23, april '23, enz.
                if (monthsToShow > 12) {
                    year = year.substring(2);
                    // bypass the problem of writing an apostrophe in a string and the eslint quote check
                    let subYear = String.fromCharCode(39);
                    year = subYear.concat(year);
                }
                return `${month} ${year}`;
            },

            getChartData() {
                const organizationId = this.organizationId;
                // Include all actions during this last day
                this.end.setHours(23, 59, 59);
                return this.getUsageChartData({
                    filter: this.chartConfig.filter,
                    organizationId,
                    type: this.chartConfig.requestType || 'customEvents',
                    start: this.start,
                    end: this.end
                });
            },

            findColumnIndex(columnName) {
                return this.resultsColumns.findIndex(
                    ({ name }) => name === columnName
                );
            }
        }
    };
</script>
